import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHotel, SwitchStage } from "../../contexts/HotelContext";
import { Building2, Check, Circle } from "lucide-react";
import { cn } from "../../lib/utils";

interface Step {
  id: SwitchStage;
  labelKey: string;
  defaultLabel: string;
}

const STEPS: Step[] = [
  { id: 'preparing', labelKey: 'hotel_switch_step_preparing', defaultLabel: 'Preparing workspace' },
  { id: 'loading', labelKey: 'hotel_switch_step_loading', defaultLabel: 'Loading hotel data' },
  { id: 'finishing', labelKey: 'hotel_switch_step_finishing', defaultLabel: 'Finishing up' },
];

/**
 * HotelSwitchOverlay - Full-screen overlay shown when switching between hotels.
 * Uses a step-based indicator instead of percentage progress for honest UX.
 */
const HotelSwitchOverlay: React.FC = () => {
  const { t } = useTranslation();
  const { isSwitching, switchStage, myHotelInfo, availableHotels, getSelectedHotelId } = useHotel();
  const [isVisible, setIsVisible] = useState(false);
  const [completedSteps, setCompletedSteps] = useState<Set<SwitchStage>>(new Set());

  // Get the target hotel name
  const targetHotelId = getSelectedHotelId();
  const targetHotel = availableHotels.find((h) => h.hotelId === targetHotelId);
  const targetHotelName = targetHotel?.hotelName || myHotelInfo?.hotelName || "Hotel";

  // Handle visibility with slight delay for smooth animation
  useEffect(() => {
    if (isSwitching) {
      setIsVisible(true);
      setCompletedSteps(new Set());
    } else {
      // Delay hiding for fade out animation
      const timer = setTimeout(() => {
        setIsVisible(false);
        setCompletedSteps(new Set());
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isSwitching]);

  // Track completed steps based on stage progression
  useEffect(() => {
    if (switchStage === 'idle') return;

    const stageOrder: SwitchStage[] = ['preparing', 'loading', 'finishing'];
    const currentIndex = stageOrder.indexOf(switchStage);

    // Mark all previous stages as completed
    if (currentIndex > 0) {
      setCompletedSteps(prev => {
        const newSet = new Set(prev);
        for (let i = 0; i < currentIndex; i++) {
          newSet.add(stageOrder[i]);
        }
        return newSet;
      });
    }
  }, [switchStage]);

  // Mark all steps complete when finishing
  useEffect(() => {
    if (!isSwitching && isVisible) {
      setCompletedSteps(new Set(['preparing', 'loading', 'finishing']));
    }
  }, [isSwitching, isVisible]);

  const getStepStatus = (stepId: SwitchStage): 'completed' | 'current' | 'pending' => {
    if (completedSteps.has(stepId)) return 'completed';
    if (stepId === switchStage) return 'current';
    return 'pending';
  };

  if (!isVisible) return null;

  return (
    <div
      className={cn(
        "fixed inset-0 z-100 flex flex-col items-center justify-center",
        "bg-background/95 backdrop-blur-xs",
        "transition-opacity duration-300",
        isSwitching ? "opacity-100" : "opacity-0"
      )}
    >
      {/* Content container with animation */}
      <div
        className={cn(
          "flex flex-col items-center text-center",
          "transition-all duration-500",
          isSwitching ? "scale-100 opacity-100" : "scale-95 opacity-0"
        )}
      >
        {/* Hotel icon with pulse animation */}
        <div className="relative mb-6">
          <Building2 className="h-16 w-16 text-primary" />
          <div className="absolute inset-0 animate-ping">
            <Building2 className="h-16 w-16 text-primary/30" />
          </div>
        </div>

        {/* Switching text */}
        <h2 className="text-xl font-medium text-muted-foreground mb-2">
          {t("hotel_switch_switching_to", "Switching to")}
        </h2>

        {/* Hotel name */}
        <h1 className="text-3xl font-bold text-foreground mb-10">
          {targetHotelName}
        </h1>

        {/* Step indicator */}
        <div className="flex flex-col gap-3 w-64">
          {STEPS.map((step, _index) => {
            const status = getStepStatus(step.id);
            return (
              <div
                key={step.id}
                className={cn(
                  "flex items-center gap-3 transition-all duration-300",
                  status === 'completed' && "opacity-60",
                  status === 'pending' && "opacity-40"
                )}
              >
                {/* Step indicator icon */}
                <div
                  className={cn(
                    "flex h-6 w-6 shrink-0 items-center justify-center rounded-full transition-all duration-300",
                    status === 'completed' && "bg-primary text-primary-foreground",
                    status === 'current' && "bg-primary/20 text-primary border-2 border-primary",
                    status === 'pending' && "bg-muted text-muted-foreground"
                  )}
                >
                  {status === 'completed' ? (
                    <Check className="h-3.5 w-3.5" />
                  ) : status === 'current' ? (
                    <Circle className="h-2.5 w-2.5 fill-current animate-pulse" />
                  ) : (
                    <Circle className="h-2 w-2" />
                  )}
                </div>

                {/* Step label */}
                <span
                  className={cn(
                    "text-sm font-medium transition-all duration-300",
                    status === 'completed' && "text-muted-foreground line-through",
                    status === 'current' && "text-foreground",
                    status === 'pending' && "text-muted-foreground"
                  )}
                >
                  {t(step.labelKey, step.defaultLabel)}
                  {status === 'current' && (
                    <span className="inline-flex ml-1">
                      <span className="animate-bounce" style={{ animationDelay: '0ms' }}>.</span>
                      <span className="animate-bounce" style={{ animationDelay: '150ms' }}>.</span>
                      <span className="animate-bounce" style={{ animationDelay: '300ms' }}>.</span>
                    </span>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default HotelSwitchOverlay;
